import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { getWordInfo } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { upperWord, lowerWord } from '../utils/letters';
import './WordInfoButton.css';

// Same thresholds as the backend's word-info frequency label.
function frequencyKey(prevalence) {
    if (prevalence >= 0.9) return 'veryCommon';
    if (prevalence >= 0.7) return 'common';
    if (prevalence >= 0.5) return 'moderate';
    if (prevalence >= 0.3) return 'uncommon';
    if (prevalence >= 0.1) return 'rare';
    return 'veryRare';
}

// DB part-of-speech codes; anything else (e.g. already-Croatian labels) is shown as-is.
const POS_KEYS = {
    noun: 'noun', verb: 'verb', adjective: 'adjective', 'adjective satellite': 'adjectiveSatellite',
    adverb: 'adverb', pronoun: 'pronoun', preposition: 'preposition', conjunction: 'conjunction',
    interjection: 'interjection', numeral: 'numeral', particle: 'particle', article: 'article',
};

/**
 * A small button that shows word information (definitions, frequency) when clicked.
 * Displays a popup with the word's definitions and other metadata.
 * Uses a Portal to render the popup at document body level for reliable positioning.
 */
function WordInfoButton({ word }) {
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [wordData, setWordData] = useState(null);
    const [error, setError] = useState(null);
    const [popupStyle, setPopupStyle] = useState({});
    const buttonRef = useRef(null);
    const popupRef = useRef(null);
    const { language, t, locale } = useLanguage();

    // Calculate popup position relative to button
    const updatePopupPosition = useCallback(() => {
        if (!buttonRef.current) return;

        const buttonRect = buttonRef.current.getBoundingClientRect();
        const popupWidth = 300;
        const padding = 8;

        // Position below the button, aligned to the right edge
        let top = buttonRect.bottom + padding;
        let left = buttonRect.right - popupWidth;

        // Keep within viewport horizontally
        if (left < padding) {
            left = padding;
        }
        if (left + popupWidth > window.innerWidth - padding) {
            left = window.innerWidth - popupWidth - padding;
        }

        // Check actual popup height if available
        const popupHeight = popupRef.current?.offsetHeight || 300;

        // If popup would go below viewport, position above button instead
        if (top + popupHeight > window.innerHeight - padding) {
            top = buttonRect.top - popupHeight - padding;
            // If still off screen, just position at top of viewport
            if (top < padding) {
                top = padding;
            }
        }

        setPopupStyle({ top: `${top}px`, left: `${left}px` });
    }, []);

    // Update position when popup opens and on scroll/resize
    useEffect(() => {
        if (isOpen) {
            // Initial position calculation
            updatePopupPosition();

            // Update on scroll (capture phase to catch all scrolls) or resize
            const handleUpdate = () => updatePopupPosition();
            window.addEventListener('scroll', handleUpdate, true);
            window.addEventListener('resize', handleUpdate);

            return () => {
                window.removeEventListener('scroll', handleUpdate, true);
                window.removeEventListener('resize', handleUpdate);
            };
        }
    }, [isOpen, updatePopupPosition]);

    // Recalculate position when content loads
    useEffect(() => {
        if (isOpen && wordData) {
            // Small delay to let content render
            requestAnimationFrame(updatePopupPosition);
        }
    }, [isOpen, wordData, updatePopupPosition]);

    // Fetch word info when popup opens
    useEffect(() => {
        if (isOpen && !wordData && !loading) {
            setLoading(true);
            setError(null);

            getWordInfo(language, lowerWord(word, language))
                .then(data => {
                    setWordData(data);
                    setLoading(false);
                })
                .catch(err => {
                    setError(err.message || t('word.loadError'));
                    setLoading(false);
                });
        }
    }, [isOpen, word, language, wordData, loading, t]);

    // Close popup when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (popupRef.current && !popupRef.current.contains(event.target) &&
                buttonRef.current && !buttonRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    // Close on escape key
    useEffect(() => {
        function handleKeyDown(event) {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        }

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isOpen]);

    const togglePopup = (e) => {
        e.stopPropagation();
        setIsOpen(!isOpen);
    };

    // Group definitions by part of speech
    const groupedDefinitions = wordData?.definitions?.reduce((acc, def) => {
        if (!acc[def.part_of_speech]) {
            acc[def.part_of_speech] = [];
        }
        acc[def.part_of_speech].push(def);
        return acc;
    }, {}) || {};

    const hasPrevalence = typeof wordData?.prevalence === 'number';
    const posLabel = (pos) => {
        const code = String(pos || '').trim().toLowerCase();
        if (!code || code === 'unknown') return null;
        return POS_KEYS[code] ? t(`word.pos.${POS_KEYS[code]}`) : pos;
    };

    // Popup content to be rendered via portal
    const popupContent = isOpen ? (
        <div ref={popupRef} className="word-info-popup" style={popupStyle}>
            <div className="word-info-header">
                <h3 className="word-info-title">{upperWord(word)}</h3>
                <button
                    className="word-info-close"
                    onClick={() => setIsOpen(false)}
                    aria-label={t('common.close')}
                >
                    &times;
                </button>
            </div>

            <div className="word-info-content">
                {loading && (
                    <div className="word-info-loading">
                        <span className="spinner"></span>
                        {t('common.loading')}
                    </div>
                )}

                {error && (
                    <div className="word-info-error">
                        {error}
                    </div>
                )}

                {wordData && !loading && (
                    <>
                        <div className="word-info-meta">
                            <span className="word-info-frequency" title={hasPrevalence ? t('word.prevalence', { percent: wordData.prevalence.toLocaleString(locale, { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 }) }) : undefined}>
                                {hasPrevalence ? t(`word.frequency.${frequencyKey(wordData.prevalence)}`) : wordData.frequencyLabel}
                            </span>
                            <span className="word-info-length">
                                {t('common.count.letters', { count: wordData.length })}
                            </span>
                        </div>

                        {wordData.morphology && (
                            <div className="word-info-morphology">{wordData.morphology}</div>
                        )}

                        {Object.keys(groupedDefinitions).length > 0 ? (
                            <div className="word-info-definitions">
                                {Object.entries(groupedDefinitions).map(([pos, defs]) => (
                                    <div key={pos} className="word-info-pos-group">
                                        {posLabel(pos) && <span className="word-info-pos">{posLabel(pos)}</span>}
                                        <ol className="word-info-def-list">
                                            {defs.map((def, idx) => (
                                                <li key={idx} className="word-info-def-item">
                                                    <span className="word-info-def-text">{def.definition}</span>
                                                    {def.example && (
                                                        <span className="word-info-example">{t('word.example', { text: def.example })}</span>
                                                    )}
                                                </li>
                                            ))}
                                        </ol>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="word-info-no-defs">
                                {t('word.noDefinitions')}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    ) : null;

    return (
        <div className="word-info-container">
            <button
                ref={buttonRef}
                className="word-info-button"
                onClick={togglePopup}
                title={t('word.buttonTitle')}
                aria-label={t('word.buttonLabel', { word })}
            >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                </svg>
            </button>

            {/* Render popup at document body level via Portal */}
            {popupContent && ReactDOM.createPortal(popupContent, document.body)}
        </div>
    );
}

export default WordInfoButton;
